import { ResponsiveTable, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { StatusBadge } from '../../_money/StatusBadge.js';
import type { Customer } from '../contractsMirror.js';
import { COLUMN_LABEL } from '../labelStyles.js';

import { PORTAL_READY } from '../portal.js';

/**
 * Clients list table. A real semantic `<table>` — Client (name) · Email · Phone ·
 * Documents (count) · Portal (ON/OFF, text not color alone) — with a `<caption>`,
 * `<th scope="col">` headers and a readable colspan empty row (A11Y-01). The name
 * cell links to the client page. Every color is a locked token (BRAND-06).
 *
 * The wrapper is the kit's `ResponsiveTable`, not a bare `overflow-x-auto` div.
 * Five columns on a 390px phone is a sideways scroller, and Clients is the first
 * app inside the platform shell — it cannot be the one that breaks on a phone.
 * `ResponsiveTable` stamps each `<td>` with its header text and the
 * `.qelm-responsive-table` block in tokens.css reflows the rows into stacked,
 * labelled cards below `md`. Desktop is untouched.
 *
 * Documents is a LEDGER column: right-aligned tabular figures, and a zero is set
 * in muted ink. In this product a client is derived from documents, so a client
 * carrying none is one the import scan found no paperwork for — the single fact
 * this screen exists to surface, readable in one scan down the column.
 */

const TH = `px-3 py-2 text-left ${COLUMN_LABEL}`;
const NUMERIC_TH = `px-3 py-2 text-right ${COLUMN_LABEL}`;

/**
 * Portal state. ON is the SIGNAL — the rare, positive case a reader scans this
 * column to find — so it gets the container and full-strength ink; OFF is plain
 * muted text with nothing around it. The hierarchy is built from weight and tone,
 * never from colour, so the meaning survives a greyscale print and the column
 * does not become a field of tinted chips (four rows in five say OFF). It is the
 * same rule `DocumentCount` below already follows for a zero.
 *
 * ⛔ This deliberately does NOT use `tone="success"`. That tone reaches --chart-3
 * through Tailwind v4's SQUARE-BRACKET form of a CSS variable, which compiles to the
 * invalid `color: --chart-3` and is DROPPED by the browser. (The pattern is not
 * written out here on purpose — the repo greps for it. v4 wants the parenthesis
 * form.) Measured in the shipped
 * screenshots: the badge lost its fill and border colour and fell back to
 * currentColor, so "On" rendered as a black (light) / white (dark) outline that
 * read as a BUTTON rather than a state, louder than anything else in the table.
 * `--chart-3` is also green, which is off the QELM palette.
 */
export function PortalStateBadge({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  if (!enabled) {
    return <span className="text-muted-foreground text-sm">{t('clients:table.portalOff')}</span>;
  }
  // `neutralBlue` carries no blue: it is `bg-secondary` + `text-secondary-foreground`,
  // the kit's solid neutral chip. Both tokens are plain utilities, not bracket vars.
  return <StatusBadge tone="neutralBlue">{t('clients:table.portalOn')}</StatusBadge>;
}

/**
 * A count of zero is a real count, not missing data — it renders the digit and
 * carries its meaning in the number, never in the tone alone. The tone only
 * makes the scan faster.
 */
function DocumentCount({ count }: { count: number }) {
  return <span className={count === 0 ? 'text-muted-foreground' : 'text-foreground'}>{count}</span>;
}

export function ClientsTable({ clients }: { clients: Customer[] }) {
  const { t } = useTranslation();

  return (
    <ResponsiveTable className="rounded-md border border-border bg-card">
      <table className="w-full border-collapse">
        <caption className="sr-only">{t('clients:table.caption')}</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className={TH}>
              {t('clients:table.client')}
            </th>
            <th scope="col" className={TH}>
              {t('clients:table.email')}
            </th>
            <th scope="col" className={TH}>
              {t('clients:table.phone')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('clients:table.documents')}
            </th>
            {PORTAL_READY ? (
              <th scope="col" className={TH}>
                {t('clients:table.portal')}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {clients.map((client) => (
            <tr
              key={client.id}
              className="border-border/60 border-b transition-colors duration-[var(--dur-fast)] last:border-b-0 hover:bg-muted/40"
            >
              <td className="px-3 py-2 text-sm">
                <Link
                  to="/clients/$id"
                  params={{ id: String(client.id) }}
                  className="font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {client.name || t('clients:table.unnamedClient', { id: client.id })}
                </Link>
              </td>
              <td className="px-3 py-2 text-foreground text-sm">
                {client.email ?? <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2 text-foreground text-sm">
                {client.phone ?? <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2 text-right text-sm tabular-nums">
                <DocumentCount count={client.document_count} />
              </td>
              {PORTAL_READY ? (
                <td className="px-3 py-2 text-sm">
                  <PortalStateBadge enabled={client.portal_enabled} />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}
